import React from 'react';
import { Player, PlayerWithLeagueContext, LeagueOutput, RatingHistory } from '../../generated-api';

interface PlayerFilterWidgetProps {
  players: (Player | PlayerWithLeagueContext)[];
  leagues: LeagueOutput[];
  playerHistories: Record<string, RatingHistory>;
  selectedLeague: string;
  minPlayCount: string;
  maxPlayCount: string;
  excludeLowConfidence?: boolean;
  onSelectedLeagueChange: (leagueId: string) => void;
  onMinPlayCountChange: (value: string) => void;
  onMaxPlayCountChange: (value: string) => void;
  onExcludeLowConfidenceChange?: (value: boolean) => void;
  onClearFilters: () => void;
  filteredPlayersCount: number;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

const PlayerFilterWidget: React.FC<PlayerFilterWidgetProps> = ({
  players,
  leagues,
  playerHistories,
  selectedLeague,
  minPlayCount,
  maxPlayCount,
  excludeLowConfidence = false,
  onSelectedLeagueChange,
  onMinPlayCountChange,
  onMaxPlayCountChange,
  onExcludeLowConfidenceChange,
  onClearFilters,
  filteredPlayersCount,
  isExpanded: controlledIsExpanded,
}) => {
  const isExpanded: boolean = controlledIsExpanded ?? false;

  const getLeagueName = (leagueId: string) => {
    const league = leagues.find(l => l.id === leagueId);
    return league ? league.name : `League ${leagueId.slice(-4)}`;
  };

  return (
    <div className={`mb-6 bg-surface shadow rounded-lg p-6 ${isExpanded ? 'block' : 'hidden'} md:block`}>
      {/* Desktop Header */}
      <h3 className="hidden md:block text-lg font-medium text-heading mb-4">Filters</h3>
      
      {/* Filter Content - Collapsible on mobile, always visible on desktop */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block transition-all duration-200`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* League Filter */}
          <div>
            <label htmlFor="league-filter" className="block text-sm font-medium text-body mb-2">
              Filter by League
            </label>
            <select
              id="league-filter"
              value={selectedLeague}
              onChange={(e) => onSelectedLeagueChange(e.target.value)}
              className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Leagues</option>
              {leagues
                .filter(league => !league.isGameSpecificLeague)
                .sort((a, b) => {
                  // Put Global league first
                  if (a.isGlobal) return -1;
                  if (b.isGlobal) return 1;
                  return a.name.localeCompare(b.name);
                })
                .map(league => (
                  <option key={league.id} value={league.id}>
                    {league.name} {league.isGlobal ? '(Global)' : ''}
                  </option>
                ))
              }
            </select>
          </div>

          {/* Minimum Play Count */}
          <div>
            <label htmlFor="min-play-count" className="block text-sm font-medium text-body mb-2">
              Minimum Plays
            </label>
            <input
              type="number"
              id="min-play-count"
              min="0"
              value={minPlayCount}
              onChange={(e) => onMinPlayCountChange(e.target.value)}
              placeholder="Min"
              className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Maximum Play Count */}
          <div>
            <label htmlFor="max-play-count" className="block text-sm font-medium text-body mb-2">
              Maximum Plays
            </label>
            <input
              type="number"
              id="max-play-count"
              min="0"
              value={maxPlayCount}
              onChange={(e) => onMaxPlayCountChange(e.target.value)}
              placeholder="Max"
              className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Confidence Filter */}
        {onExcludeLowConfidenceChange && (
          <div className="mt-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={excludeLowConfidence}
                onChange={(e) => onExcludeLowConfidenceChange(e.target.checked)}
                className="h-4 w-4 text-primary-600 border-edge-input rounded focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-body">Exclude low confidence ratings</span>
            </label>
            <p className="text-xs text-muted mt-1 ml-6">
              Hide players with fewer than 5 games or high uncertainty (σ &gt; 5)
            </p>
          </div>
        )}
      </div>

      {/* Filter Results Summary - Collapsible on mobile, always visible on desktop */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block mt-4 pt-4 border-t border-edge flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-0`}>
        <p className="text-sm text-body">
          Showing {filteredPlayersCount} of {players.length} players
          {selectedLeague && (
            <span className="ml-1">
              in <strong>{getLeagueName(selectedLeague)}</strong>
            </span>
          )}
          {(minPlayCount || maxPlayCount) && (
            <span className="ml-1">
              with <strong>
                {minPlayCount && maxPlayCount
                  ? `${minPlayCount}-${maxPlayCount} plays`
                  : minPlayCount
                  ? `≥${minPlayCount} plays`
                  : `≤${maxPlayCount} plays`}
              </strong>
            </span>
          )}
          {excludeLowConfidence && (
            <span className="ml-1">
              (<strong>excluding uncertain ratings</strong>)
            </span>
          )}
        </p>
        {(selectedLeague || minPlayCount || maxPlayCount || excludeLowConfidence) && (
          <button
            onClick={onClearFilters}
            className="text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            Clear Filters
          </button>
        )}
      </div>
    </div>
  );
};

export default PlayerFilterWidget;

